import { useCallback, useEffect, useRef, useState } from "react";
import {
  getCustomisation,
  listTemplates,
  resetCustomisation,
  saveCustomisation,
} from "./emailTemplatesApi";

/**
 * Loads the customisable templates and the selected one's slot values.
 *
 * Reads are race-guarded: switching templates quickly must not let a slower
 * earlier response overwrite the newer one.
 * @param {string} templateKey the template currently being edited.
 * @returns {object} templates, slots, loading/error state and mutations.
 */
export function useTemplateCustomisation(templateKey) {
  const [templates, setTemplates] = useState([]);
  const [slots, setSlots] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const fetchId = useRef(0);

  useEffect(() => {
    listTemplates().then((res) => {
      if (res.isSuccess) setTemplates(res.data || []);
    });
  }, []);

  const load = useCallback(() => {
    if (!templateKey) return;
    const id = ++fetchId.current;
    setLoading(true);
    setError(null);
    getCustomisation(templateKey).then((res) => {
      if (id !== fetchId.current) return;
      setLoading(false);
      if (res.isSuccess) setSlots(res.data);
      else setError(res.message || "Could not load this template");
    });
  }, [templateKey]);

  useEffect(load, [load]);

  /**
   * Saves the slot values for the selected template.
   * @param {object} form the slot values.
   * @returns {Promise<{ok: boolean, message?: string}>}
   */
  const save = useCallback(
    async (form) => {
      const res = await saveCustomisation({ templateKey, ...form });
      if (res.isSuccess) setSlots(res.data);
      return { ok: res.isSuccess, message: res.message };
    },
    [templateKey],
  );

  /**
   * Reverts the selected template to its shipped defaults.
   * @returns {Promise<{ok: boolean, message?: string}>}
   */
  const reset = useCallback(async () => {
    const res = await resetCustomisation(templateKey);
    if (res.isSuccess) setSlots(res.data);
    return { ok: res.isSuccess, message: res.message };
  }, [templateKey]);

  return { templates, slots, loading, error, save, reset, refresh: load };
}
